import React, { useState } from "react";
import axios from "axios";
import { isProductLive } from "../config/iGetToCourse";

/**
 * BuyOrWaitlist — the universal "Buy" component for any I GET TO product.
 *
 * Renders:
 *  • Real Stripe Buy button when product.stripeLink is set
 *  • Waitlist email-capture (anchors to #waitlist) when link is still placeholder
 *
 * Use this in every landing page (Course 1, Course 2, Bundle).
 */

const BRAND = {
  deep: "#042F2E",
  gold: "#d4af6a",
  cream: "#efeae7",
  mint: "#5EEAD4",
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function BuyButton({ product, size = "lg", testId = "buy-cta" }) {
  const LIVE = isProductLive(product);
  const sizes = {
    lg: { padding: "22px 44px", fontSize: "17px" },
    md: { padding: "18px 36px", fontSize: "15px" },
    sm: { padding: "14px 28px", fontSize: "14px" },
  };

  if (LIVE) {
    return (
      <a
        href={product.stripeLink}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-3 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02] hover:shadow-2xl"
        style={{ background: BRAND.mint, color: BRAND.deep, ...sizes[size] }}
        data-testid={testId}
      >
        Start now · {product.priceDisplay}
        <span style={{ fontSize: "18px" }}>→</span>
      </a>
    );
  }

  return (
    <a
      href="#waitlist"
      className="inline-flex items-center gap-3 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02] hover:shadow-2xl"
      style={{ background: BRAND.gold, color: BRAND.deep, ...sizes[size] }}
      data-testid={`${testId}-waitlist`}
    >
      Be first to know · {product.priceDisplay}
      <span style={{ fontSize: "18px" }}>↓</span>
    </a>
  );
}

export function WaitlistForm({ product, testIdPrefix = "waitlist" }) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    setErrorMsg("");
    try {
      await axios.post(`${API_URL}/api/subscribe`, {
        email: email.trim().toLowerCase(),
        firstName: firstName.trim() || "Friend",
        group_id: product.waitlistGroup,
      });
      setStatus("success");
    } catch (err) {
      const code = err?.response?.status;
      if (code === 400 || code === 409) {
        setStatus("success");
      } else {
        setStatus("error");
        setErrorMsg(
          err?.response?.data?.detail ||
            "Something hiccupped. Please try again in a moment."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (status === "success") {
    return (
      <div
        className="rounded-2xl p-7 max-w-xl mx-auto"
        style={{ background: `${BRAND.mint}1a`, border: `1px solid ${BRAND.mint}` }}
        data-testid={`${testIdPrefix}-success`}
      >
        <p className="font-serif italic text-xl" style={{ color: BRAND.cream }}>
          You're on the list. ✦
        </p>
        <p className="font-sans text-sm mt-2" style={{ color: `${BRAND.cream}cc` }}>
          The moment checkout opens, you'll be the first to know — and the first to start.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
      data-testid={`${testIdPrefix}-form`}
    >
      <input
        type="text"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        placeholder="First name (optional)"
        className="sm:w-48 px-5 py-3 rounded-full font-sans text-sm outline-none"
        style={{ background: "rgba(255,255,255,0.95)", color: BRAND.deep }}
        data-testid={`${testIdPrefix}-first-name`}
      />
      <input
        type="email"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="your email"
        className="flex-1 px-5 py-3 rounded-full font-sans text-sm outline-none"
        style={{ background: "rgba(255,255,255,0.95)", color: BRAND.deep }}
        data-testid={`${testIdPrefix}-email`}
      />
      <button
        type="submit"
        disabled={submitting}
        className="inline-flex items-center justify-center gap-2 px-7 py-3 rounded-full font-sans font-semibold tracking-wide transition-all hover:scale-[1.02] disabled:opacity-60 whitespace-nowrap"
        style={{ background: BRAND.mint, color: BRAND.deep, fontSize: "14px" }}
        data-testid={`${testIdPrefix}-submit`}
      >
        {submitting ? "Sending…" : "Notify me — it's almost here"}
        <span style={{ fontSize: "16px" }}>→</span>
      </button>
      {status === "error" && (
        <p
          className="font-sans text-xs basis-full text-center"
          style={{ color: "#fda4af" }}
          data-testid={`${testIdPrefix}-error`}
        >
          {errorMsg}
        </p>
      )}
    </form>
  );
}
